import React, { useContext, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Stack, Typography, Button, Tooltip } from '@mui/material';
import useWrittenDirectives from '../../../_data_sources/WrittenDirectives/useWrittenDirectives';
import { CircularProgress } from '@mui/material';
import JurisdictionPicker from 'components/_pickers/JurisdictionPicker';
import { Jurisdiction } from 'types/jurisdiction';
import { neutral } from '_lex/_foundations/phxColors';
import { ChevronRight, Add } from '@mui/icons-material';
import { AppContext } from 'views/App/context';
import { WrittenDirective } from '../../../../_types/awb';
import { translate as t } from 'hooks/useTranslations';
import RightDrawer from '_lex/_layouts/TakeoverLayout/RightDrawer';

interface WrittenDirectivesSummaryProps {
  standardId: string;
  jurisdictions: Jurisdiction[];
}

const WrittenDirectivesSummary: React.FC<WrittenDirectivesSummaryProps> = ({ standardId, jurisdictions }) => {
  const { id: accreditationId, versionId } = useParams();
  const navigate = useNavigate();
  const [selectedJurisdictionId, setSelectedJurisdictionId] = useState<string>(jurisdictions[0]?.id ?? '');
  const { currentOrganizationId, hasPermission } = useContext(AppContext);
  const {
    data: writtenDirectives,
    isLoading,
    isFetching,
  } = useWrittenDirectives(currentOrganizationId, accreditationId!, versionId!, standardId, selectedJurisdictionId);

  const canCreateWrittenDirective = hasPermission('createWrittenDirective', currentOrganizationId);

  const headerActions = (
    <Tooltip
      title={t('awb.standards.detail.rightDrawer.add')}
      placement='left'
    >
      <Button
        size='small'
        className='xs icon-only'
        variant='outlined'
        color='neutral'
        startIcon={<Add />}
        disabled={!canCreateWrittenDirective}
        onClick={() =>
          navigate(
            `/admin/accreditation/${accreditationId}/versions/${versionId}/standards/${standardId}/jurisdiction/${selectedJurisdictionId}/written-directives`
          )
        }
      />
    </Tooltip>
  );

  return (
    <RightDrawer
      header={t('awb.standards.detail.rightDrawer.title')}
      headerActions={headerActions}
      content={
        <Stack spacing={2}>
          {jurisdictions.length > 1 && (
            <Box sx={{ py: 2 }}>
              <JurisdictionPicker
                id='jurisdiction-picker'
                value={[selectedJurisdictionId]}
                onChange={(value) => setSelectedJurisdictionId(value[0])}
                allowBlank={false}
                allowMultiple={false}
                filterIds={jurisdictions.map((j) => j.id)}
                size='small'
                sx={{ pt: 0 }}
                prefix='Jurisdiction'
              />
            </Box>
          )}
          {isLoading || isFetching ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : !writtenDirectives?.length ? (
            <Box sx={{ py: 2 }}>
              <Typography
                variant='bodySm'
                color='text.secondary'
              >
                No written directives found
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2}>
              {writtenDirectives.map((directive: WrittenDirective) => (
                <Box
                  key={directive.id}
                  onClick={() =>
                    navigate(
                      `/admin/accreditation/${accreditationId}/versions/${versionId}/standards/${standardId}/jurisdiction/${selectedJurisdictionId}/written-directives/${directive.id}`
                    )
                  }
                  sx={{
                    py: 2,
                    borderBottom: `1px solid ${neutral[100]}`,
                    display: 'grid',
                    gridTemplateColumns: '1fr auto',
                    gap: 2,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: neutral[50],
                    },
                  }}
                >
                  <Stack sx={{ minWidth: 0 }}>
                    <Typography
                      variant='labelSm'
                      noWrap
                      sx={{
                        color: neutral[500],
                        fontWeight: 500,
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                      }}
                    >
                      {directive.contentHeading} {directive.contentTitle}
                    </Typography>
                    <Typography
                      variant='bodySm'
                      noWrap
                      sx={{
                        color: neutral[500],
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                      }}
                    >
                      {directive.manualTitle}
                    </Typography>
                  </Stack>
                  <ChevronRight sx={{ color: neutral[500], fontSize: 24 }} />
                </Box>
              ))}
            </Stack>
          )}
        </Stack>
      }
      open={true}
    />
  );
};

export default WrittenDirectivesSummary;
